import styled from '@emotion/styled';
import React from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import mainContext from '../../../contexts/mainContext';
import { __constants, __snackBarSeverity } from '../../../constants';
import {
    getExternalReviewProofController,
    approveExternalReviewItemController,
    editExternalReviewController,
    deleteExternalReviewController,
} from '../../../Controllers/userManagment/externalReview';

const emptyForm = { comment: '', name: '', sourcePlatform: '' };

const ExternalReviews = ({ userId }) => {
    const { setSnackBar, nth, months } = React.useContext(mainContext);
    const [items, setItems] = React.useState([]);
    const [openDialog, setOpenDialog] = React.useState(false);
    const [dialogMode, setDialogMode] = React.useState('approve'); // 'approve' or 'edit'
    const [activeItem, setActiveItem] = React.useState(null);
    const [form, setForm] = React.useState(emptyForm);
    const [loading, setLoading] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);

    const callSnackBar = (msg, type) => {
        setSnackBar({ status: true, text: msg, severity: __snackBarSeverity[type] });
    };

    const fetchProof = async () => {
        const res = await getExternalReviewProofController(userId);
        if (res?.data?.status === 200) {
            const proof = res.data.data.externalReviewProof || { screenshots: [], links: [] };
            const merged = [
                ...(proof.screenshots || []).map((s) => ({ ...s, itemType: 'screenshot' })),
                ...(proof.links || []).map((l) => ({ ...l, itemType: 'link' })),
            ].sort((a, b) => (a.status === b.status ? 0 : a.status === 'Pending' ? -1 : 1));
            setItems(merged);
        }
    };

    React.useEffect(() => {
        if (userId) fetchProof();
        // eslint-disable-next-line
    }, [userId]);

    const handleOpenApprove = (item) => {
        setDialogMode('approve');
        setActiveItem(item);
        setForm(emptyForm);
        setOpenDialog(true);
    };

    const handleOpenEdit = (item) => {
        setDialogMode('edit');
        setActiveItem(item);
        setForm({
            comment: item.reviewId?.comment || '',
            name: item.reviewId?.name || '',
            sourcePlatform: item.reviewId?.sourcePlatform || '',
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setActiveItem(null);
        setForm(emptyForm);
    };

    const handleSave = async () => {
        if (!form.comment.trim()) return callSnackBar('Review text is required', 'error');
        if (!form.sourcePlatform.trim()) return callSnackBar('Source platform is required', 'error');

        setLoading(true);
        try {
            const res = dialogMode === 'approve'
                ? await approveExternalReviewItemController({
                    userId,
                    itemType: activeItem.itemType,
                    itemId: activeItem._id,
                    comment: form.comment,
                    name: form.name,
                    sourcePlatform: form.sourcePlatform,
                })
                : await editExternalReviewController(activeItem.reviewId._id, {
                    comment: form.comment,
                    name: form.name,
                    sourcePlatform: form.sourcePlatform,
                });

            if (res?.data?.status === 200) {
                callSnackBar(dialogMode === 'approve' ? 'Review approved and published' : 'Review updated', 'success');
                fetchProof();
                handleCloseDialog();
            } else {
                callSnackBar(res?.data?.errors?.[0]?.message || 'Something went wrong', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setLoading(true);
        try {
            const res = await deleteExternalReviewController(deleteTarget.reviewId._id);
            if (res?.data?.status === 200) {
                callSnackBar('Review deleted — item moved back to pending', 'success');
                fetchProof();
            } else {
                callSnackBar(res?.data?.errors?.[0]?.message || 'Something went wrong', 'error');
            }
        } finally {
            setLoading(false);
            setDeleteTarget(null);
        }
    };

    return (
        <Container>
            <strong className="title">External Reviews</strong>
            <small className="subtitle">Screenshots/links the housekeeper submitted from their profile.</small>

            {items.length === 0 ? (
                <small>Nothing submitted yet</small>
            ) : (
                items.map((item) => {
                    const isPending = item.status === 'Pending';
                    const preview = item.itemType === 'screenshot'
                        ? (
                            <PreviewLink href={__constants.images + item.name} target="_blank" rel="noreferrer">
                                <ImageOutlinedIcon fontSize="small" />
                                View screenshot
                            </PreviewLink>
                        )
                        : (
                            <PreviewLink href={item.url} target="_blank" rel="noreferrer">
                                <LinkOutlinedIcon fontSize="small" />
                                {item.url}
                            </PreviewLink>
                        );

                    return (
                        <ItemCard key={item._id}>
                            <Row>
                                {preview}
                                <Chip
                                    size="small"
                                    label={isPending ? 'Pending' : 'Approved'}
                                    variant="outlined"
                                    sx={isPending ? {} : {
                                        color: '#4C8C6B',
                                        borderColor: 'rgba(76, 140, 107, 0.4)',
                                        backgroundColor: 'rgba(76, 140, 107, 0.08)',
                                    }}
                                />
                            </Row>

                            {isPending ? (
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                                    onClick={() => handleOpenApprove(item)}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        mt: 1,
                                        color: '#4C8C6B',
                                        backgroundColor: 'rgba(76, 140, 107, 0.1)',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        '&:hover': { backgroundColor: 'rgba(76, 140, 107, 0.18)' },
                                    }}
                                >
                                    Approve &amp; write review
                                </Button>
                            ) : (
                                <ReviewBlock>
                                    <Chip size="small" label={`External Review — ${item.reviewId?.sourcePlatform}`} color="primary" variant="outlined" />
                                    <p>{item.reviewId?.comment}</p>
                                    <div className="meta">
                                        <span>by {item.reviewId?.name || 'Anonymous'} · {item.reviewId?.createdAt ? `${new Date(item.reviewId.createdAt).getDate()}${nth(new Date(item.reviewId.createdAt).getDate())} ${months[new Date(item.reviewId.createdAt).getMonth()]} ${new Date(item.reviewId.createdAt).getFullYear()}` : ''}</span>
                                        <span>
                                            <IconButton size="small" onClick={() => handleOpenEdit(item)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => setDeleteTarget(item)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </div>
                                </ReviewBlock>
                            )}
                        </ItemCard>
                    );
                })
            )}

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': { overflowX: 'hidden' },
                    // The app has a global `* { box-sizing: unset !important }` rule that breaks
                    // MUI's border-box sizing, making outlined fields (esp. multiline) overflow
                    // their container by a few px. Force border-box back on just these fields.
                    '& .MuiOutlinedInput-root, & .MuiOutlinedInput-root *, & .MuiInputBase-input': {
                        boxSizing: 'border-box !important',
                    },
                }}
            >
                <DialogTitle>{dialogMode === 'approve' ? 'Approve & Write Review' : 'Edit External Review'}</DialogTitle>
                <DialogContent sx={{ overflowX: 'hidden' }}>
                    <FieldLabel>Review text</FieldLabel>
                    <TextField
                        autoFocus
                        margin="dense"
                        placeholder="What did they say about this housekeeper?"
                        fullWidth
                        multiline
                        minRows={3}
                        value={form.comment}
                        onChange={(e) => setForm({ ...form, comment: e.target.value.slice(0, 500) })}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${form.comment.length}/500`}
                        sx={{ mb: 2, mt: 0.5 }}
                    />
                    <FieldLabel>Reviewer name (optional)</FieldLabel>
                    <TextField
                        margin="dense"
                        placeholder="e.g. Carol"
                        fullWidth
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value.slice(0, 20) })}
                        inputProps={{ maxLength: 20 }}
                        helperText={`${form.name.length}/20`}
                        sx={{ mb: 2, mt: 0.5 }}
                    />
                    <FieldLabel>Source platform</FieldLabel>
                    <TextField
                        margin="dense"
                        placeholder="e.g. TaskRabbit, Care.com"
                        fullWidth
                        value={form.sourcePlatform}
                        onChange={(e) => setForm({ ...form, sourcePlatform: e.target.value.slice(0, 20) })}
                        inputProps={{ maxLength: 20 }}
                        helperText={`${form.sourcePlatform.length}/20`}
                        sx={{ mt: 0.5 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={loading}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={18} /> : null}
                    >
                        {loading ? 'Saving...' : (dialogMode === 'approve' ? 'Approve & Publish' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete this review?</DialogTitle>
                <DialogContent>The screenshot/link will move back to Pending — you can approve it again later.</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={loading}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={loading}>
                        {loading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const FieldLabel = styled('div')`
    font-size: 80%;
    font-weight: 600;
    color: var(--nona-theme-color, #7d90a4);
    margin-top: 14px;
`;

const Container = styled('div')`
    display: grid;
    row-gap: 0.6rem;
    .title{
        color: var(--nona-theme-color);
        font-size: 90%;
    }
    .subtitle{
        opacity: 0.7;
        font-size: 80%;
    }
`;

const ItemCard = styled('div')`
    background: rgba(0,0,0,0.03);
    border-radius: 8px;
    padding: 0.6rem 0.8rem;
    display: flex;
    flex-direction: column;
`;

const Row = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    font-size: 85%;
    a{
        word-break: break-all;
    }
`;

const PreviewLink = styled('a')`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--nona-theme-color, #f98f53);
    text-decoration: underline !important;
    cursor: pointer;
    svg{
        font-size: 16px;
    }
    &:hover{
        opacity: 0.8;
    }
`;

const ReviewBlock = styled('div')`
    margin-top: 0.5rem;
    p{
        margin-top: 0.3rem;
        font-family: "regular";
        font-size: 95%;
    }
    .meta{
        margin-top: 0.3rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 70%;
        font-family: "semi-bold";
    }
`;

export default ExternalReviews;
